import type { AppConfig } from '../config';
import { codeChallenge, createCodeVerifier, randomString } from './pkce';
import {
  PKCE_KEY,
  storageGet,
  storageRemove,
  storageSet,
  toTokenSet,
  type TokenResponse,
  type TokenSet,
} from './tokens';

/** Cognito Managed Login with the authorization code flow and PKCE. No Amplify. */

export type CognitoConfig = Pick<AppConfig, 'cognitoDomain' | 'cognitoClientId' | 'redirectUri' | 'logoutUri'>;

export const SCOPES = 'openid email';

export function authorizeUrl(cfg: CognitoConfig, state: string, challenge: string): string {
  const q = new URLSearchParams({
    response_type: 'code',
    client_id: cfg.cognitoClientId,
    redirect_uri: cfg.redirectUri,
    scope: SCOPES,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  return `${cfg.cognitoDomain}/oauth2/authorize?${q}`;
}

export function logoutUrl(cfg: CognitoConfig): string {
  const q = new URLSearchParams({ client_id: cfg.cognitoClientId, logout_uri: cfg.logoutUri });
  return `${cfg.cognitoDomain}/logout?${q}`;
}

/** Creates verifier and state, remembers them for the callback and returns the authorize URL. */
export async function beginSignIn(cfg: CognitoConfig): Promise<string> {
  const verifier = createCodeVerifier();
  const state = randomString(32);
  storageSet(PKCE_KEY, JSON.stringify({ verifier, state }));
  return authorizeUrl(cfg, state, await codeChallenge(verifier));
}

export class AuthError extends Error {
  /** True when the service could not be reached (offline, laptop just woke up): the session may still be valid. */
  readonly offline: boolean;
  constructor(message: string, offline = false) {
    super(message);
    this.name = 'AuthError';
    this.offline = offline;
  }
}

async function tokenRequest(
  cfg: CognitoConfig,
  form: Record<string, string>,
  fetchImpl: typeof fetch,
): Promise<TokenResponse> {
  let res: Response;
  try {
    res = await fetchImpl(`${cfg.cognitoDomain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(form).toString(),
    });
  } catch {
    throw new AuthError('Could not reach the sign-in service. Check your connection.', true);
  }
  // 5xx and throttling are temporary; 4xx (invalid_grant: refresh token expired or revoked) ends the session
  if (!res.ok)
    throw new AuthError('Sign-in did not work. Please sign in again.', res.status >= 500 || res.status === 429);
  return (await res.json()) as TokenResponse;
}

/** Exchanges the code from the callback. Checks `state` against the stored value. */
export async function completeSignIn(
  cfg: CognitoConfig,
  params: { code: string | null; state: string | null; error?: string | null },
  fetchImpl: typeof fetch = fetch,
  now: number = Date.now(),
): Promise<TokenSet> {
  if (params.error) throw new AuthError('Sign-in was cancelled or failed. Please try again.');
  const raw = storageGet(PKCE_KEY);
  storageRemove(PKCE_KEY);
  let stored: { verifier?: string; state?: string } = {};
  try {
    stored = raw ? (JSON.parse(raw) as typeof stored) : {};
  } catch {
    stored = {};
  }
  if (!params.code || !stored.verifier || !params.state || params.state !== stored.state) {
    throw new AuthError('This sign-in link is no longer valid. Please sign in again.');
  }
  const res = await tokenRequest(
    cfg,
    {
      grant_type: 'authorization_code',
      client_id: cfg.cognitoClientId,
      code: params.code,
      redirect_uri: cfg.redirectUri,
      code_verifier: stored.verifier,
    },
    fetchImpl,
  );
  return toTokenSet(res, now);
}

export async function refreshTokens(
  cfg: CognitoConfig,
  tokens: TokenSet,
  fetchImpl: typeof fetch = fetch,
  now: number = Date.now(),
): Promise<TokenSet> {
  if (!tokens.refreshToken) throw new AuthError('Session expired. Please sign in again.');
  const res = await tokenRequest(
    cfg,
    { grant_type: 'refresh_token', client_id: cfg.cognitoClientId, refresh_token: tokens.refreshToken },
    fetchImpl,
  );
  return toTokenSet(res, now, tokens);
}
